import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { LEAD_TAB } from '~/api/API_PATH';
import { SEARCH_BANNER_FIELDS } from '~/utils/constant';
import pagingStore from '~/stores/lead/pagingStore';
import { FieldType } from '~/types/mst';

const IDENTIFIER = 'leadSearchModel';

/**
 * 리드목록 검색 모델
 */
const model = types
  .model(IDENTIFIER, {
    /** 스토어 아이덴티티 */
    identifier: types.optional(types.identifier, IDENTIFIER),
    /** 상세조회 검색시 토클용 */
    toggleSearch: types.optional(types.boolean, false),
    /** 리스트에서 선택한 item id */
    selectedItemId: types.maybeNull(types.number),
    /** 상담중만 기간만료 여부를 필터링하고 나머지(미연락, 출고, 반환)는 모두 true
     * true: 기간만료 상담건 함께보기, false: 기간만료 제외 상담건만 보기 */
    isExpired: types.optional(types.boolean, false),
    /** 1: 미연락, 2: 상담중, 5: 출고, 7: 반환 */
    status: types.maybeNull(types.number),
    /** 검색 키워드 */
    searchKey: types.optional(types.string, ''),
    /** 검색 시작 날짜 */
    estimateStartDt: types.optional(types.string, ''),
    /** 검색 종료 날짜 */
    estimateEndDt: types.optional(types.string, ''),
    /** 전체: null, 현금: 1 할부: 2 */
    buyType: types.maybeNull(types.number),
    /** 차량 브랜드 id */
    idCarbrand: types.maybeNull(types.number),
    /** 차량 등급 id */
    idCargrade: types.maybeNull(types.number),
    /** 차량 모델 id */
    idCarmodel: types.maybeNull(types.number),
    /** paging model */
    pagingModel: types.optional(pagingStore.model, () => pagingStore.create),
    /** 조회기간 선택 타입 */
    termType: types.optional(types.string, 'all'),
    /** 선택한 차량 브랜드명 */
    carBrandName: types.optional(types.string, ''),
    /** 선택한 차량 모델명 */
    carModelName: types.optional(types.string, ''),
    /** 검색영역 초기화용 트리거 */
    searchAreaInitTrigger: types.optional(types.boolean, false),
    /** 출고 상태 */
    releaseStatus: types.optional(types.string, ''),
  })
  .actions((self) => {
    /** field object type */
    type fieldObjType = FieldType<typeof self>;

    return {
      /** 초기화 */
      setInit() {
        applySnapshot(self, defaultValue);
      },
      /** 상세검색에서 사용했던 필드값 초기화 */
      setInitAllSearchField() {
        self.searchKey = '';
        self.estimateStartDt = '';
        self.estimateEndDt = '';
        self.termType = 'all';
        self.idCarbrand = null;
        self.carBrandName = '';
        self.idCarmodel = null;
        self.carModelName = '';
        self.buyType = null;
      },
      /** 검색영역 필드 초기화 */
      setInitField(field: typeof SEARCH_BANNER_FIELDS[number]) {
        if (field === 'KEYWORD') {
          self.searchKey = '';
        }

        if (field === 'TERM') {
          self.estimateStartDt = '';
          self.estimateEndDt = '';
          self.termType = 'all';
        }

        if (field === 'BRAND') {
          self.idCarbrand = null;
          self.carBrandName = '';
        }

        if (field === 'MODEL') {
          self.idCarmodel = null;
          self.carModelName = '';
        }

        if (field === 'BUY_TYPE') {
          self.buyType = null;
        }
      },

      // 키워드 검색
      setSearchKeyword(val: string) {
        self.searchKey = val;
      },

      /** set 리드목록 텝 선택시 상태값 변경 */
      setStatus(val: LEAD_TAB) {
        // 탭 상태 set
        self.status = val;

        // 기간만료 상담건 함께 보기 초기화
        self.isExpired = false;

        // 선택한 아이템 번호 초기화
        self.selectedItemId = 0;

        // 페이징 초기화
        self.pagingModel.setInit();

        // self.searchKey = '';
      },
      /** set 선택한 아이템 id */
      setSelectedItemId(val: number) {
        self.selectedItemId = val === self.selectedItemId ? 0 : val;
      },
      /** set 기간만료 상담건 함께보기 */
      setExpired(val: boolean) {
        self.isExpired = val;
      },
      /** set 상세조회 토글 */
      setToggleSearch() {
        self.toggleSearch = !self.toggleSearch;
      },
      /** search field all set */
      setSearchAll(searchObj: fieldObjType) {
        // 페이징 초기화
        self.pagingModel.setInit();

        Object.entries(searchObj).forEach(([key, value]) => {
          self[key] = value;
        });
      },
      /** 검색영역 초기화용 (리드목록에서 라우트 이동시 강제 초기화 해주기 위함) */
      setSearchAreaInit() {
        self.searchAreaInitTrigger = !self.searchAreaInitTrigger;
      },
      setReleaseStatus(status: string) {
        self.releaseStatus = status;
      },
    };
  })
  .views((self) => ({
    /** 검색필드 배너 보일지 여부 */
    getIsSearchBannerShow() {
      return (
        !!self.searchKey.trim() ||
        self.termType !== 'all' ||
        !!self.idCarbrand ||
        !!self.idCarmodel ||
        !!self.buyType
      );
    },
    /** get 모든 필드 */
    getAllfield() {
      return getSnapshot(self);
    },
    /** get api 호출시 필요한 인자값 */
    getApiVal(status?: number | null) {
      return {
        offset: self.pagingModel.pageSize,
        page: self.pagingModel.pageNo,
        status: status ?? self.status,
        det_cd: null,
        search: self.searchKey,
        estimate_start_date: self.estimateStartDt,
        estimate_end_date: self.estimateEndDt,
        buy_type: self.buyType,
        id_carbrand: self.idCarbrand,
        id_cargrade: self.idCargrade,
        id_carmodel: self.idCarmodel,
        is_expired: status === 2 ? self.isExpired : status !== 1,
        release_status: status === 5 ? self.releaseStatus : '',
      };
    },
  }));

/** 초기화 값 */
const defaultValue: SnapshotIn<typeof model> = {
  identifier: IDENTIFIER,
  toggleSearch: false,
  isExpired: false,
  selectedItemId: null,
  status: 1,
  searchKey: '',
  estimateStartDt: '',
  estimateEndDt: '',
  buyType: null,
  idCarbrand: null,
  idCargrade: null,
  idCarmodel: null,
  termType: 'all',
  carBrandName: '',
  carModelName: '',
  pagingModel: { ...pagingStore.defaultValue },
  searchAreaInitTrigger: false,
};

/** create or initialize */
const create = model.create(defaultValue);

/** store */
const store = {
  create,
  defaultValue,
  model,
};

/** 모델 타입 */
export type ILeadModelType = Instance<typeof model>;

export default store;
